import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Currency, Money } from '@principle-theorem/accounting';
import { TimezoneService } from '@principle-theorem/ng-principle-shared';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  type AppointmentSummary,
  type DepositRequestDeliveryChannel,
  type IAppointment,
  type IDepositMetadata,
} from '@principle-theorem/principle-core/interfaces';
import {
  CASUAL_DATE_WITH_YEAR,
  Firestore,
  formatTimeFromTo,
  shareReplayCold,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SendAppointmentDepositLinkService } from '../../scheduling/deposit/send-appointment-deposit-link.service';
import {
  depositRecordedMessage,
  TakeDepositPanelComponent,
} from '../appointment-deposit-sidebar/take-deposit-panel/take-deposit-panel.component';
import { TakeDepositEntry } from '../take-deposit-entry';

export interface ITakeDepositDialogData {
  appointment: AppointmentSummary;
  entry: TakeDepositEntry;
}

export interface IAppointmentEventDisplay {
  date: string;
  time: string;
}

/**
 * The focused dialog for taking a deposit on an appointment that was just
 * created.
 *
 * "Save Appointment & Take Deposit" used to land the operator in the full
 * appointment view sidebar, which buries the one thing they asked for under
 * tabs, patient detail and treatment plans. This surface carries only what the
 * next action needs: who and when the appointment is for, what is owed, and the
 * capture panel itself.
 *
 * Sending a payment link is handed to a root-scoped service and the dialog
 * closes at once, so nothing here tracks a send in flight.
 */
@Component({
  selector: 'pr-take-deposit-dialog',
  templateUrl: './take-deposit-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule, MatTooltipModule, TakeDepositPanelComponent],
})
export class TakeDepositDialogComponent {
  private readonly _timezone = inject(TimezoneService);
  private readonly _dialogRef =
    inject<MatDialogRef<TakeDepositDialogComponent>>(MatDialogRef);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _sendLink = inject(SendAppointmentDepositLinkService);
  readonly entry: TakeDepositEntry;
  readonly patientName: string;
  readonly eventDisplay$: Observable<IAppointmentEventDisplay>;
  appointment$: Observable<WithRef<IAppointment>>;
  depositAmount$: Observable<string | undefined>;

  constructor(@Inject(MAT_DIALOG_DATA) data: ITakeDepositDialogData) {
    this.entry = data.entry;
    this.patientName = data.appointment.metadata.label;
    this.eventDisplay$ = this._timezone
      .getEventRange$(data.appointment.event)
      .pipe(
        map((range) => ({
          date: range.from.format(CASUAL_DATE_WITH_YEAR),
          time: formatTimeFromTo(range.from, range.to),
        })),
        shareReplayCold()
      );

    if (!data.appointment.ref) {
      return;
    }
    this.appointment$ = Firestore.doc$(data.appointment.ref).pipe(
      shareReplayCold()
    );
    this.depositAmount$ = this.appointment$.pipe(
      map((appointment) =>
        TakeDepositDialogComponent._formatAmount(appointment.deposit?.metadata)
      )
    );
  }

  private static _formatAmount(deposit?: IDepositMetadata): string | undefined {
    if (!deposit) {
      return;
    }
    return Money.formatPlainCents(
      deposit.amountCents,
      deposit.currency ?? Currency.AUD
    );
  }

  /**
   * Leaves the focused flow with the one confirmation. The panel reports the
   * outcome explicitly, so nothing here re-reads the appointment to guess it.
   */
  depositCaptured(): void {
    this._dialogRef.close();
    this._snackBar.open(depositRecordedMessage, undefined, { duration: 5000 });
  }

  /** Leaves the focused flow without claiming a deposit was taken. */
  depositSkipped(): void {
    this._dialogRef.close();
  }

  /**
   * Closes at once and lets the send finish elsewhere. The sending, sent and
   * failed states all arrive as snackbars from the link service, so choosing a
   * channel never holds the operator in the dialog.
   */
  sendPaymentLink(
    appointment: WithRef<IAppointment>,
    deliveryChannel: DepositRequestDeliveryChannel
  ): void {
    this._dialogRef.close();
    void this._sendLink.send(appointment, deliveryChannel);
  }
}
